import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import healthApi from '@/api/healthApi';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { ArrowLeft, ArrowRight, Smile, Frown, Zap, CloudRain, Activity, Droplet, Moon, Dumbbell, Heart, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import PrivacyBadge from '@/components/PrivacyBadge';
import SuccessAnimation from '@/components/SuccessAnimation';
import HealthScoreCircle from '@/components/HealthScoreCircle';
import RiskStrengthPanel from '@/components/RiskStrengthPanel';
import MicroRecommendations from '@/components/MicroRecommendations';
import DailyHealthInsight from '@/components/DailyHealthInsight';

const moods = [
  { value: 'happy', label: 'Happy', emoji: '😊', color: 'bg-green-100 border-green-300 text-green-700' },
  { value: 'energetic', label: 'Energetic', emoji: '⚡', color: 'bg-yellow-100 border-yellow-300 text-yellow-700' },
  { value: 'tired', label: 'Tired', emoji: '😴', color: 'bg-blue-100 border-blue-300 text-blue-700' },
  { value: 'stressed', label: 'Stressed', emoji: '😰', color: 'bg-orange-100 border-orange-300 text-orange-700' },
  { value: 'anxious', label: 'Anxious', emoji: '😟', color: 'bg-purple-100 border-purple-300 text-purple-700' },
  { value: 'sad', label: 'Sad', emoji: '😢', color: 'bg-gray-100 border-gray-300 text-gray-700' },
];

const stressLevels = [
  { value: 'low', label: 'Low', emoji: '😌', color: 'bg-green-100 border-green-400 text-green-800' },
  { value: 'medium', label: 'Medium', emoji: '😐', color: 'bg-yellow-100 border-yellow-400 text-yellow-800' },
  { value: 'high', label: 'High', emoji: '😣', color: 'bg-red-100 border-red-400 text-red-800' },
];

const appetiteLevels = [
  { value: 'low', label: 'Low', emoji: '🍽️', description: 'Not very hungry', color: 'bg-orange-100 border-orange-300 text-orange-700' },
  { value: 'normal', label: 'Normal', emoji: '😋', description: 'Usual appetite', color: 'bg-green-100 border-green-300 text-green-700' },
  { value: 'increased', label: 'Increased', emoji: '🍴', description: 'More hungry', color: 'bg-blue-100 border-blue-300 text-blue-700' },
];

const commonSymptoms = [
  'Headache', 'Fatigue', 'Nausea', 'Dizziness', 'Pain', 'Fever', 'Cough', 'Insomnia'
];

const MultiStepCheckIn = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [alreadyCheckedIn, setAlreadyCheckedIn] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [intelligence, setIntelligence] = useState(null);
  const [formData, setFormData] = useState({
    mood: '',
    stress_level: 'low',
    energy_level: 5,
    sleep_quality: 5,
    appetite: 'normal',
    symptoms: [],
    other_symptoms: '',
    medications_taken_today: false,
    exercise_completed_today: 0,
    water_intake_today: 0,
    notes: ''
  });

  const totalSteps = 4;

  useEffect(() => {
    checkTodayCheckIn();
  }, []);

  const checkTodayCheckIn = async () => {
    try {
      const { data } = await healthApi.getCheckIns(1);
      const today = new Date().toISOString().split('T')[0];
      if (data.length > 0 && data[0].date === today) {
        setAlreadyCheckedIn(true);
      }
    } catch (error) {
      console.error('Error checking today check-in:', error);
    }
  };

  const handleSymptomToggle = (symptom) => {
    setFormData(prev => ({
      ...prev,
      symptoms: prev.symptoms.includes(symptom)
        ? prev.symptoms.filter(s => s !== symptom)
        : [...prev.symptoms, symptom]
    }));
  };

  const validateStep = (step) => {
    if (step === 1 && !formData.mood) {
      toast.error('Please select your mood');
      return false;
    }
    return true;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, totalSteps));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!formData.mood) {
      toast.error('Please select your mood');
      return;
    }

    setLoading(true);
    try {
      const response = await healthApi.createCheckIn(formData);
      
      // Store the enhanced intelligence feedback
      setIntelligence(response.intelligence || {});
      
      // Show success animation
      setShowSuccess(true);
      
      setTimeout(() => {
        toast.success('Health intelligence generated! 🎉');
      }, 1500);
    } catch (error) {
      console.error('Check-in error:', error);
      
      if (error.response?.status === 401) {
        toast.error('Please log in to complete check-in');
        setTimeout(() => navigate('/login'), 1500);
        return;
      }
      
      const detail = error.response?.data?.detail;
      let errorMsg = 'Failed to record check-in';
      
      if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (detail?.msg) {
        errorMsg = detail.msg;
      } else if (detail?.message) {
        errorMsg = detail.message;
      }
      
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Already checked in view
  if (alreadyCheckedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 p-4">
        <div className="max-w-2xl mx-auto pt-8">
          <Card className="border-2 border-green-300 bg-green-50">
            <CardHeader>
              <CardTitle className="flex items-center text-green-800">
                <Activity className="h-6 w-6 mr-2" />
                Already Checked In Today! ✅
              </CardTitle>
              <CardDescription className="text-green-700">
                You've completed today's wellness snapshot. See you tomorrow!
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button onClick={() => navigate('/dashboard')} className="bg-green-600 hover:bg-green-700">
                Back to Home
              </Button>
              <Button 
                onClick={() => navigate('/health-insights')} 
                variant="outline"
                className="ml-2 border-green-600 text-green-700"
              >
                View Health Insights
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Intelligence Results View
  if (showSuccess && intelligence) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 p-4">
        <SuccessAnimation message="Health intelligence generated!" onComplete={() => {}} />
        
        <div className="max-w-6xl mx-auto pt-8 pb-8">
          {/* Header */}
          <div className="text-center mb-6">
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
              Your Daily Health Intelligence 🧠
            </h1>
            <p className="text-gray-600">
              Comprehensive insights powered by AI analysis
            </p>
          </div>

          <div className="space-y-6">
            {/* Daily Health Insight */}
            <DailyHealthInsight />

            {/* Daily Health Score */}
            <HealthScoreCircle 
              score={intelligence.daily_health_score}
              interpretation={intelligence.score_interpretation}
              components={intelligence.score_components}
            />

            {/* Risks and Strengths */}
            <RiskStrengthPanel 
              risks={intelligence.risks}
              strengths={intelligence.strengths}
            />

            {/* Micro Recommendations */}
            <MicroRecommendations 
              recommendations={intelligence.micro_recommendations}
            />

            {/* Summary Feedback */}
            {intelligence.feedback && (
              <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-indigo-200">
                <CardContent className="p-6">
                  <div className="flex items-start gap-3">
                    <Sparkles className="h-6 w-6 text-indigo-600 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-lg font-bold text-indigo-900 mb-2">Today's Summary</h3>
                      <p className="text-gray-800 leading-relaxed">{intelligence.feedback}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Encouragement */}
            {intelligence.encouragement && (
              <Card className="bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-200">
                <CardContent className="p-6">
                  <div className="flex items-start gap-3">
                    <Smile className="h-6 w-6 text-purple-600 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-lg font-bold text-purple-900 mb-2">Daily Encouragement</h3>
                      <p className="text-gray-800 text-lg italic">"{intelligence.encouragement}"</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <Button 
                onClick={() => navigate('/health-insights')} 
                className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 py-6 text-lg"
              >
                View My Health Insights Dashboard
              </Button>
              <Button 
                onClick={() => navigate('/dashboard')} 
                variant="outline"
                className="flex-1 border-2 border-indigo-300 hover:bg-indigo-50 py-6 text-lg"
              >
                Back to Home
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Multi-Step Check-In Form
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-4">
      <div className="max-w-3xl mx-auto pt-4 pb-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/dashboard')}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Home
        </Button>

        <Card className="shadow-xl border-2 border-indigo-200">
          <CardHeader className="bg-gradient-to-r from-indigo-100 to-purple-100">
            <PrivacyBadge message="Your check-ins are private and confidential" icon="lock" />
            <CardTitle className="text-2xl flex items-center mt-3">
              <Activity className="h-7 w-7 mr-3 text-indigo-600" />
              Daily Health Intelligence Check-In
            </CardTitle>
            <CardDescription className="text-base">
              Step {currentStep} of {totalSteps}: Complete your wellness snapshot to receive personalized insights
            </CardDescription>
            
            {/* Progress Bar */}
            <div className="mt-4">
              <Progress value={(currentStep / totalSteps) * 100} className="h-2" />
              <div className="flex justify-between mt-2 text-xs text-gray-600">
                <span>Mood & Energy</span>
                <span>Sleep & Hydration</span>
                <span>Symptoms</span>
                <span>Activity</span>
              </div>
            </div>
          </CardHeader>

          <CardContent className="pt-6">
            {/* Step 1: Mood & Energy */}
            {currentStep === 1 && (
              <div className="space-y-6 animate-fadeIn">
                <div className="space-y-3">
                  <Label className="text-lg font-semibold flex items-center">
                    <Smile className="h-5 w-5 mr-2 text-indigo-600" />
                    How's your mood today?
                  </Label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {moods.map((mood) => {
                      const isSelected = formData.mood === mood.value;
                      return (
                        <button
                          key={mood.value}
                          type="button"
                          onClick={() => setFormData({ ...formData, mood: mood.value })}
                          className={`p-4 rounded-xl border-2 transition-all transform hover:scale-105 ${
                            isSelected
                              ? mood.color + ' ring-2 ring-offset-2 ring-indigo-400 shadow-lg'
                              : 'bg-white border-gray-200 hover:border-indigo-300'
                          }`}
                        >
                          <div className="text-3xl mb-2">{mood.emoji}</div>
                          <p className="text-sm font-medium">{mood.label}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="space-y-3">
                  <Label className="text-lg font-semibold flex items-center">
                    <CloudRain className="h-5 w-5 mr-2 text-indigo-600" />
                    Stress Level
                  </Label>
                  <div className="grid grid-cols-3 gap-3">
                    {stressLevels.map((level) => {
                      const isSelected = formData.stress_level === level.value;
                      return (
                        <button
                          key={level.value}
                          type="button"
                          onClick={() => setFormData({ ...formData, stress_level: level.value })}
                          className={`p-4 rounded-xl border-2 transition-all transform hover:scale-105 ${
                            isSelected
                              ? level.color + ' ring-2 ring-offset-2 ring-indigo-400 shadow-lg'
                              : 'bg-white border-gray-200 hover:border-indigo-300'
                          }`}
                        >
                          <div className="text-3xl mb-2">{level.emoji}</div>
                          <p className="text-sm font-medium">{level.label}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="space-y-3 p-4 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl">
                  <div className="flex justify-between items-center">
                    <Label className="text-lg font-semibold flex items-center">
                      <Zap className="h-5 w-5 mr-2 text-orange-600" />
                      Energy Level
                    </Label>
                    <span className="text-2xl font-bold text-orange-600">
                      {formData.energy_level}/10
                    </span>
                  </div>
                  <Slider
                    value={[formData.energy_level]}
                    onValueChange={([value]) => setFormData({ ...formData, energy_level: value })}
                    min={1}
                    max={10}
                    step={1}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-gray-600">
                    <span>Very Low</span>
                    <span>Very High</span>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Sleep & Hydration */}
            {currentStep === 2 && (
              <div className="space-y-6 animate-fadeIn">
                <div className="space-y-3 p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl">
                  <div className="flex justify-between items-center">
                    <Label className="text-lg font-semibold flex items-center">
                      <Moon className="h-5 w-5 mr-2 text-purple-600" />
                      Sleep Quality (Last Night)
                    </Label>
                    <span className="text-2xl font-bold text-purple-600">
                      {formData.sleep_quality}/10
                    </span>
                  </div>
                  <Slider
                    value={[formData.sleep_quality]}
                    onValueChange={([value]) => setFormData({ ...formData, sleep_quality: value })}
                    min={1}
                    max={10}
                    step={1}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-gray-600">
                    <span>Very Poor</span>
                    <span>Excellent</span>
                  </div>
                </div>

                <div className="p-4 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl space-y-2">
                  <Label htmlFor="water" className="text-base font-semibold flex items-center">
                    <Droplet className="h-5 w-5 mr-2 text-blue-600" />
                    Water Intake Today (Liters)
                  </Label>
                  <Input
                    id="water"
                    type="number"
                    step="0.1"
                    min="0"
                    max="10"
                    value={formData.water_intake_today}
                    onChange={(e) => setFormData({ ...formData, water_intake_today: parseFloat(e.target.value) || 0 })}
                    placeholder="0.0"
                    className="text-lg"
                  />
                  <p className="text-xs text-gray-600">Target: 2L per day for optimal hydration</p>
                </div>

                <div className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl space-y-2">
                  <Label htmlFor="exercise" className="text-base font-semibold flex items-center">
                    <Dumbbell className="h-5 w-5 mr-2 text-green-600" />
                    Exercise Today (Minutes)
                  </Label>
                  <Input
                    id="exercise"
                    type="number"
                    min="0"
                    max="1440"
                    value={formData.exercise_completed_today}
                    onChange={(e) => setFormData({ ...formData, exercise_completed_today: parseInt(e.target.value) || 0 })}
                    placeholder="0"
                    className="text-lg"
                  />
                  <p className="text-xs text-gray-600">Target: 30 minutes per day for health benefits</p>
                </div>
              </div>
            )}

            {/* Step 3: Symptoms & Appetite */}
            {currentStep === 3 && (
              <div className="space-y-6 animate-fadeIn">
                <div className="space-y-3">
                  <Label className="text-lg font-semibold flex items-center">
                    <Heart className="h-5 w-5 mr-2 text-pink-600" />
                    Appetite Today
                  </Label>
                  <div className="grid grid-cols-3 gap-3">
                    {appetiteLevels.map((level) => {
                      const isSelected = formData.appetite === level.value;
                      return (
                        <button
                          key={level.value}
                          type="button"
                          onClick={() => setFormData({ ...formData, appetite: level.value })}
                          className={`p-4 rounded-xl border-2 transition-all transform hover:scale-105 ${
                            isSelected
                              ? level.color + ' ring-2 ring-offset-2 ring-pink-400 shadow-lg'
                              : 'bg-white border-gray-200 hover:border-pink-300'
                          }`}
                        >
                          <div className="text-3xl mb-2">{level.emoji}</div>
                          <p className="text-sm font-medium">{level.label}</p>
                          <p className="text-xs text-gray-500 mt-1">{level.description}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="space-y-3">
                  <Label className="text-lg font-semibold">Any Symptoms Today?</Label>
                  <p className="text-sm text-gray-600">Optional - Select any symptoms you're experiencing</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {commonSymptoms.map((symptom) => (
                      <div 
                        key={symptom} 
                        className={`p-3 rounded-lg border-2 transition-all ${
                          formData.symptoms.includes(symptom)
                            ? 'bg-red-100 border-red-300'
                            : 'bg-white border-gray-200 hover:border-red-200'
                        }`}
                      >
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id={symptom}
                            checked={formData.symptoms.includes(symptom)}
                            onCheckedChange={() => handleSymptomToggle(symptom)}
                          />
                          <label
                            htmlFor={symptom}
                            className="text-sm font-medium cursor-pointer select-none"
                          >
                            {symptom}
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <Input
                    placeholder="Other symptoms (optional)"
                    value={formData.other_symptoms}
                    onChange={(e) => setFormData({ ...formData, other_symptoms: e.target.value })}
                    className="w-full mt-3"
                  />
                </div>
              </div>
            )}

            {/* Step 4: Notes */}
            {currentStep === 4 && (
              <div className="space-y-6 animate-fadeIn">
                <div className="space-y-3">
                  <Label htmlFor="notes" className="text-lg font-semibold">Additional Notes (Optional)</Label>
                  <Textarea
                    id="notes"
                    placeholder="Anything else you'd like to note about your day? This helps us provide better insights..."
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={5}
                    className="resize-none"
                  />
                </div>

                {/* Summary Preview */}
                <Card className="bg-gradient-to-br from-indigo-50 to-purple-50 border-2 border-indigo-200">
                  <CardHeader>
                    <CardTitle className="text-base">Your Check-In Summary</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="flex items-center gap-2">
                        <Smile className="h-4 w-4 text-indigo-600" />
                        <span>Mood: <strong className="capitalize">{formData.mood || 'Not set'}</strong></span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Zap className="h-4 w-4 text-orange-600" />
                        <span>Energy: <strong>{formData.energy_level}/10</strong></span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Moon className="h-4 w-4 text-purple-600" />
                        <span>Sleep: <strong>{formData.sleep_quality}/10</strong></span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Droplet className="h-4 w-4 text-blue-600" />
                        <span>Water: <strong>{formData.water_intake_today}L</strong></span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Dumbbell className="h-4 w-4 text-green-600" />
                        <span>Exercise: <strong>{formData.exercise_completed_today} min</strong></span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CloudRain className="h-4 w-4 text-gray-600" />
                        <span>Stress: <strong className="capitalize">{formData.stress_level}</strong></span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex gap-3 mt-8 pt-6 border-t">
              {currentStep > 1 && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={prevStep}
                  className="flex-1"
                  disabled={loading}
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Previous
                </Button>
              )}
              
              {currentStep < totalSteps ? (
                <Button
                  type="button"
                  onClick={nextStep}
                  className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
                >
                  Next Step
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              ) : (
                <Button
                  type="button"
                  onClick={handleSubmit}
                  className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 py-6 text-lg font-semibold"
                  disabled={loading}
                >
                  {loading ? 'Generating Intelligence...' : 'Generate My Health Intelligence ✨'}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MultiStepCheckIn;
